import { createContext, useContext, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { useGetEventAnalytics } from 'legacy/events/hooks';
import { EventAnalytics, EventForHost } from 'legacy/events/types';

interface EventDashboardContextI {
  data: {
    event: EventForHost;
    eventAnalytics: EventAnalytics;
    isManageGuestsModalOpen: boolean;
    isManageEventModalOpen: boolean;
    hasRequestList: boolean;
  };
  goBack: () => void;
  goToEventView: () => void;
  toggleManageGuestsModal: () => void;
  toggleManageEventModal: () => void;
}

const EventDashboardContext = createContext({} as EventDashboardContextI);

function EventDashboardProvider({ children, event }) {
  const { t } = useTranslation(['events']);
  const [isManageGuestsModalOpen, setIsManageGuestsModalOpen] = useState(false);
  const [isManageEventModalOpen, setIsManageEventModalOpen] = useState(false);
  const router = useRouter();

  const hasRequestList = useMemo(
    () =>
      event?.isOrganizer &&
      event?.pricing?.tiers?.find((tier) => tier.requiresApproval),
    [event?.isOrganizer, event?.pricing?.tiers]
  );

  const { data: eventAnalytics } = useGetEventAnalytics(
    event._id,
    event.isOrganizer
  );

  const goBack = () => {
    router.back();
  };

  const goToEventView = () => {
    router.push(`/events/${event._id}`);
  };

  const toggleManageGuestsModal = () => {
    setIsManageGuestsModalOpen((prevState) => !prevState);
  };

  const toggleManageEventModal = () => {
    setIsManageEventModalOpen((manageEventOpen) => !manageEventOpen);
  };

  return (
    <EventDashboardContext.Provider
      value={{
        data: {
          event: {
            ...event,
            title: event.title || t('events:dashboard.context.untitledEvent')
          },
          eventAnalytics,
          isManageGuestsModalOpen,
          isManageEventModalOpen,
          hasRequestList
        },
        goBack,
        goToEventView,
        toggleManageGuestsModal,
        toggleManageEventModal
      }}
    >
      {children}
    </EventDashboardContext.Provider>
  );
}

export const useEventDashboard = () => useContext(EventDashboardContext);

export default EventDashboardProvider;
